// 입력 처리 클래스
class InputHandler {
  constructor() {
    this.keys = {
      w: false,
      a: false,
      s: false,
      d: false,
    };

    this.callbacks = {};

    // 조이스틱 관련 상태
    this.joystick = {
      active: false,
      x: 0,
      y: 0,
      baseX: 0,
      baseY: 0,
      maxDistance: 40, // 조이스틱이 움직일 수 있는 최대 거리
    };

    this.setupEventListeners();
    this.setupJoystick();
  }

  // 이벤트 리스너 설정
  setupEventListeners() {
    document.addEventListener("keydown", (event) => this.onKeyDown(event));
    document.addEventListener("keyup", (event) => this.onKeyUp(event));
  }

  // 조이스틱 설정
  setupJoystick() {
    const joystickContainer = document.getElementById("joystickContainer");
    const joystickStick = document.getElementById("joystickStick");
    const joystickInputArea = document.getElementById("joystickInputArea");

    if (!joystickContainer || !joystickStick || !joystickInputArea) return;

    // 조이스틱 표시
    const showJoystick = (x, y) => {
      this.joystick.baseX = x;
      this.joystick.baseY = y;
      joystickContainer.style.left = `${x}px`;
      joystickContainer.style.top = `${y}px`;
      joystickContainer.classList.add("active");
      joystickStick.classList.add("active");
    };

    // 조이스틱 숨김
    const hideJoystick = () => {
      joystickContainer.classList.remove("active");
      joystickStick.classList.remove("active");
      joystickStick.style.transform = "translate(-50%, -50%)";
      this.joystick.active = false;
      this.joystick.x = 0;
      this.joystick.y = 0;
    };

    // 조이스틱 업데이트
    const updateJoystick = (clientX, clientY) => {
      if (!this.joystick.active) return;

      const deltaX = clientX - this.joystick.baseX;
      const deltaY = clientY - this.joystick.baseY;

      // 거리 및 각도 계산
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const maxDistance = this.joystick.maxDistance;

      // 조이스틱 이동 제한
      let moveX = deltaX;
      let moveY = deltaY;

      if (distance > maxDistance) {
        const angle = Math.atan2(deltaY, deltaX);
        moveX = Math.cos(angle) * maxDistance;
        moveY = Math.sin(angle) * maxDistance;
      }

      // 조이스틱 스틱 위치 업데이트
      joystickStick.style.transform = `translate(calc(-50% + ${moveX}px), calc(-50% + ${moveY}px))`;

      // 정규화된 조이스틱 값 저장 (-1 ~ 1)
      this.joystick.x = moveX / maxDistance;
      this.joystick.y = moveY / maxDistance;
    };

    // 터치 시작
    joystickInputArea.addEventListener(
      "touchstart",
      (e) => {
        // 게임이 일시정지 상태인 경우 조이스틱 생성 방지
        if (this.isGamePaused()) {
          return;
        }

        // UI 요소를 터치한 경우 조이스틱 생성 방지
        if (this.isUIElement(e.target)) {
          return;
        }

        const touch = e.touches[0];
        this.joystick.active = true;
        showJoystick(touch.clientX, touch.clientY);
      },
      { passive: true }
    );

    // 터치 이동
    joystickInputArea.addEventListener(
      "touchmove",
      (e) => {
        const touch = e.touches[0];
        updateJoystick(touch.clientX, touch.clientY);
      },
      { passive: true }
    );

    // 터치 종료
    const endTouch = (e) => {
      if (!this.joystick.active) return;
      hideJoystick();
    };

    joystickInputArea.addEventListener("touchend", endTouch, { passive: true });
    joystickInputArea.addEventListener("touchcancel", endTouch, {
      passive: true,
    });

    // 마우스 시작 (웹에서 클릭)
    joystickInputArea.addEventListener("mousedown", (e) => {
      // 게임이 일시정지 상태인 경우 조이스틱 생성 방지
      if (this.isGamePaused()) {
        return;
      }

      // UI 요소를 클릭한 경우 조이스틱 생성 방지
      if (this.isUIElement(e.target)) {
        return;
      }

      this.joystick.active = true;
      showJoystick(e.clientX, e.clientY);
    });

    // 마우스 이동
    document.addEventListener("mousemove", (e) => {
      if (!this.joystick.active) return;
      updateJoystick(e.clientX, e.clientY);
    });

    // 마우스 종료
    const endMouse = (e) => {
      if (!this.joystick.active) return;
      hideJoystick();
    };

    document.addEventListener("mouseup", endMouse);
  }

  // 키 누름 이벤트
  onKeyDown(event) {
    const keyPressed = this.getKeyFromCode(event.code);

    if (keyPressed && this.keys.hasOwnProperty(keyPressed)) {
      this.keys[keyPressed] = true;
    }

    // 콜백 실행
    this.executeCallback("keydown", event.code, event);
  }

  // 키 뗌 이벤트
  onKeyUp(event) {
    const keyPressed = this.getKeyFromCode(event.code);

    if (keyPressed && this.keys.hasOwnProperty(keyPressed)) {
      this.keys[keyPressed] = false;
    }

    // 콜백 실행
    this.executeCallback("keyup", event.code, event);
  }

  // 키 코드를 키 이름으로 변환
  getKeyFromCode(code) {
    return GameConfig.INPUT.MOVEMENT_KEYS[code];
  }

  // 특정 키가 눌려있는지 확인
  isKeyPressed(key) {
    return this.keys[key] || false;
  }

  // 이동 키가 눌려있는지 확인
  isMoving() {
    return (
      this.keys.w ||
      this.keys.a ||
      this.keys.s ||
      this.keys.d ||
      this.joystick.active
    );
  }

  // 이동 벡터 계산
  getMovementVector(speed = 1) {
    const moveVector = new THREE.Vector3(0, 0, 0);

    // 조이스틱 입력 우선 처리
    if (this.joystick.active) {
      moveVector.x = this.joystick.x * speed;
      moveVector.z = this.joystick.y * speed;
    } else {
      // 키보드 입력 처리
      if (this.keys.w) moveVector.z -= speed;
      if (this.keys.s) moveVector.z += speed;
      if (this.keys.a) moveVector.x -= speed;
      if (this.keys.d) moveVector.x += speed;
    }

    return moveVector;
  }

  // 키 이벤트 콜백 등록
  onKey(eventType, keyCode, callback) {
    const key = `${eventType}_${keyCode}`;
    if (!this.callbacks[key]) {
      this.callbacks[key] = [];
    }
    this.callbacks[key].push(callback);
  }

  // 키 이벤트 콜백 제거
  offKey(eventType, keyCode, callback) {
    const key = `${eventType}_${keyCode}`;
    if (this.callbacks[key]) {
      const index = this.callbacks[key].indexOf(callback);
      if (index > -1) {
        this.callbacks[key].splice(index, 1);
      }
    }
  }

  // 콜백 실행
  executeCallback(eventType, keyCode, event) {
    const key = `${eventType}_${keyCode}`;
    if (this.callbacks[key]) {
      this.callbacks[key].forEach((callback) => {
        try {
          callback(event);
        } catch (error) {
          console.error("키 이벤트 콜백 실행 중 오류:", error);
        }
      });
    }
  }

  // 키 상태 리셋
  resetKeys() {
    Object.keys(this.keys).forEach((key) => {
      this.keys[key] = false;
    });
  }

  // 조이스틱 강제 숨김 (게임 일시정지 시 사용)
  hideJoystick() {
    if (!this.joystick.active) return;

    const joystickContainer = document.getElementById("joystickContainer");
    const joystickStick = document.getElementById("joystickStick");

    if (joystickContainer && joystickStick) {
      joystickContainer.classList.remove("active");
      joystickStick.classList.remove("active");
      joystickStick.style.transform = "translate(-50%, -50%)";
    }

    this.joystick.active = false;
    this.joystick.x = 0;
    this.joystick.y = 0;
  }

  // 디버그용: 현재 눌린 키 출력
  getActiveKeys() {
    return Object.keys(this.keys).filter((key) => this.keys[key]);
  }

  // 키 상태 로깅
  logKeyStates() {
    const activeKeys = this.getActiveKeys();
    if (activeKeys.length > 0) {
      Utils.log("현재 눌린 키:", activeKeys.join(", "));
    }
  }

  // UI 요소 확인 (조이스틱 생성 방지용)
  isUIElement(element) {
    // gameUI 내부의 요소인지 확인
    const gameUI = document.getElementById("gameUI");
    if (gameUI && gameUI.contains(element)) {
      return true;
    }

    // 특정 클래스나 ID를 가진 UI 요소 확인
    if (element.closest("#pauseButton, .upgrade-card, button, .ui-element")) {
      return true;
    }

    return false;
  }

  // 게임 일시정지 상태 확인
  isGamePaused() {
    const game = window.game;
    if (!game || !game.gameLoop) {
      return false;
    }

    return game.gameLoop.getStatus().isPaused;
  }
}

// webpack 빌드를 위해 default export 추가
export default InputHandler;
